import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useRef, useState} from 'react';

import {trackPanelSearchCleared} from '../../../services/analytics';
import {usePanelSearchInputContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {usePanelGenSearchErrorMessage} from '../../../state/panelGenHistoryKeys/panelGenHistoryKeysStore';
import {observeCriticalWorkflowInteraction} from '../../../util/datadog/criticalWorkflows/useCriticalWorkflowTrigger';
import {
  type PanelSearchInputEditContext,
  type PanelSearchInputSearchContext,
} from '../../../util/datadog/workspace/panelSearchInput';
import {usePanelBankScrollRoot} from '../store';
import {type OptionTypeBase} from '../types';
import {SearchHistory} from './SearchHistory';
import {trackSearchHistory} from './searchHistoryEvents';

export const PanelSearchInput: React.FC = () => {
  const {searchQuery, onChangeSearchQuery, onAddToHistory, searchHistory} =
    usePanelSearchInputContext();
  const searchErrorMessage = usePanelGenSearchErrorMessage();
  const panelBankScrollRoot = usePanelBankScrollRoot();

  // User pain is different for typing, clear, history selection, paste, and
  // corrections. SearchHistory reports those cues separately from the value
  // change, so carry the context into the next setInputValue call.
  const nextSearchContextRef = useRef<PanelSearchInputSearchContext>({
    inputSource: 'typing',
  });

  const [selectedOption, setSelectedOption] = useState<OptionTypeBase | null>(
    null
  );

  const onInteraction = useCallback((event: React.SyntheticEvent) => {
    observeCriticalWorkflowInteraction({
      workflow: 'panel-search',
      windowDurationMs: 6_000,
      event,
    });
  }, []);

  const setSearchInputValue = useCallback(
    (newSearchQuery: string) => {
      onChangeSearchQuery(newSearchQuery, nextSearchContextRef.current);
      // Reset after the search callback consumes this one-shot context so
      // clear/history/beforeinput metadata cannot leak into the next change.
      nextSearchContextRef.current = {inputSource: 'typing'};
    },
    [onChangeSearchQuery]
  );

  const handleBeforeInput = useCallback(
    (
      nativeEvent: InputEvent,
      input: HTMLInputElement | HTMLTextAreaElement
    ) => {
      const inputType =
        typeof nativeEvent.inputType === 'string' ? nativeEvent.inputType : '';
      const selectionStart = input.selectionStart ?? input.value.length;
      const selectionEnd = input.selectionEnd ?? selectionStart;

      // Capture edit shape so the vital can distinguish simple appends from
      // higher-friction changes like editing before the current end, replacing
      // selected text, or pasting a query:
      // - `losss|win` -> `loss|win` records hadDeletion + hadNonAppendEdit
      // - paste records hadPaste
      // - typing over selected text records hadReplacement
      // Cursor moves/selection changes without a value change do not count.
      // Clear/history selections use inputSource instead of this edit context.
      const editContext: PanelSearchInputEditContext = {
        hadDeletion: inputType.startsWith('delete'),
        hadNonAppendEdit: selectionStart < input.value.length,
        hadPaste: inputType === 'insertFromPaste',
        hadReplacement:
          selectionEnd > selectionStart ||
          inputType === 'insertReplacementText',
      };
      nextSearchContextRef.current = {editContext, inputSource: 'typing'};
    },
    []
  );

  const onClear = () => {
    nextSearchContextRef.current = {inputSource: 'clear'};
    trackPanelSearchCleared();
  };

  const onBlur = () => {
    // when to add to search history is a bit tricky - we chose blur since
    // it is a reasonable proxy for "the user finished editing the search",
    // and trying to save after X seconds of pause could be wrong if a
    // user stopped to think of something while typing a search.
    if (!searchQuery) {
      return;
    }
    onAddToHistory?.(searchQuery);
    trackSearchHistory({
      action: 'Item added',
      location: 'panel bank search',
    });
  };

  const onOptionChange = (newValue: OptionTypeBase | string) => {
    const newOption =
      typeof newValue === 'string'
        ? {value: newValue, label: newValue}
        : newValue;
    if (typeof newValue !== 'string') {
      nextSearchContextRef.current = {inputSource: 'history_item'};
    }
    setSelectedOption(newOption);
  };

  return (
    <Tailwind style={{width: '100%'}}>
      <SearchHistory
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        searchHistory={searchHistory}
        onOptionChange={onOptionChange}
        onBlur={onBlur}
        setInputValue={setSearchInputValue}
        inputValue={searchQuery}
        placeholderText={'Search panels with regex'}
        ddActionName="Search panels"
        perfAction="panel-search"
        onInteraction={onInteraction}
        onClear={onClear}
        onNativeBeforeInput={handleBeforeInput}
        maxItems={10}
        focusOnPageLoad={true}
        dataTest="panel-search-input"
        error={Boolean(searchErrorMessage)}
        errorMessage={searchErrorMessage}
        scrollRoot={panelBankScrollRoot}
      />
    </Tailwind>
  );
};
